import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, ShieldCheck, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { JURISDICTIONS } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { claimant_name: "", respondent_name: "", claim_value: "", jurisdiction: JURISDICTIONS[0], contact_email: "", dispute_summary: "" };

const inputCls = "w-full border border-navy-border bg-navy-deep/70 px-4 py-3 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-gold";

export const IntakeModal = ({ open, onClose }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/intake`, {
        ...form,
        claim_value: parseFloat(form.claim_value),
      });
      toast.success(t.intake.successTitle.replace("{ref}", data.reference), {
        description: t.intake.successBody,
        duration: 9000,
      });
      setForm(EMPTY);
      onClose();
    } catch (err) {
      toast.error(t.intake.errorTitle, {
        description: err.response?.data?.detail?.[0]?.msg || t.intake.errorBody,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="case-intake-modal" className="fixed inset-0 z-[110] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-navy-deep/85 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto border border-gold/30 bg-navy-card shadow-2xl">
            <div className="flex items-start justify-between border-b border-navy-border px-7 py-6 lg:px-9">
              <div>
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                  <ShieldCheck className="h-3.5 w-3.5" /> {t.intake.channel}
                </div>
                <h3 className="mt-2 font-serif text-2xl font-medium text-slate-50 lg:text-3xl">{t.intake.title}</h3>
                <p className="mt-1.5 text-sm text-slate-400">{t.intake.desc}</p>
              </div>
              <button onClick={onClose} data-testid="intake-close-button" aria-label="Close"
                className="p-1.5 text-slate-400 transition-colors hover:text-gold">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={submit} data-testid="case-intake-form" className="grid gap-5 px-7 py-7 sm:grid-cols-2 lg:px-9">
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.intake.claimant}</label>
                <input required minLength={2} value={form.claimant_name} onChange={set("claimant_name")}
                  data-testid="intake-claimant-name" placeholder={t.intake.phClaimant} className={inputCls} />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.intake.respondent}</label>
                <input required minLength={2} value={form.respondent_name} onChange={set("respondent_name")}
                  data-testid="intake-respondent-name" placeholder={t.intake.phRespondent} className={inputCls} />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.intake.claimValue}</label>
                <input required type="number" min="1" value={form.claim_value} onChange={set("claim_value")}
                  data-testid="intake-claim-value" placeholder={t.intake.phClaim} className={inputCls} />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.intake.jurisdiction}</label>
                <select value={form.jurisdiction} onChange={set("jurisdiction")}
                  data-testid="intake-jurisdiction" className={`${inputCls} appearance-none`}>
                  {JURISDICTIONS.map((j) => <option key={j} value={j} className="bg-navy-deep">{j}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.intake.email}</label>
                <input required type="email" value={form.contact_email} onChange={set("contact_email")}
                  data-testid="intake-contact-email" placeholder="legal@yourcompany.com" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.intake.summary}</label>
                <textarea required minLength={10} rows={4} value={form.dispute_summary} onChange={set("dispute_summary")}
                  data-testid="intake-dispute-summary"
                  placeholder={t.intake.phSummary}
                  className={`${inputCls} resize-none`} />
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={submitting} data-testid="intake-submit-button"
                  className="flex w-full items-center justify-center gap-3 bg-gold py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-navy-deep transition-colors duration-300 hover:bg-gold-hover disabled:opacity-60">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {submitting ? t.intake.submitting : t.intake.submit}
                </button>
                <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.15em] leading-relaxed text-slate-500">
                  {t.intake.footnote}
                </p>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
